import type {
  CasePathologistAssignmentError,
  ReplacementCandidate,
  ReplacePathologistResponse,
} from "../types/casePathologistAssignment";

export interface ReplacementCandidateOption {
  value: string;
  label: string;
}

const typedErrorGuidance: Record<
  Exclude<
    CasePathologistAssignmentError["code"],
    "PATHOLOGIST_ASSIGNMENT_STALE"
  >,
  string
> = {
  SAME_PATHOLOGIST:
    "This pathologist is already assigned to the case. Choose a different eligible pathologist.",
  REPLACEMENT_PATHOLOGIST_INELIGIBLE:
    "The selected pathologist is no longer eligible for this case. Choose another eligible pathologist.",
  ACTIVE_REVIEW_ACKNOWLEDGEMENT_REQUIRED:
    "An active review is in progress. Acknowledge the interruption before trying again.",
  IDEMPOTENCY_KEY_REUSED:
    "This request identifier was already used for another change. Refresh the case before trying again.",
  CURRENT_PATHOLOGIST_REQUIRED:
    "This case has no current pathologist to replace. Refresh the case and use the initial assignment workflow.",
};

export function assignmentErrorGuidance(
  error: CasePathologistAssignmentError
): string {
  if (error.code === "PATHOLOGIST_ASSIGNMENT_STALE") {
    return "The assignment changed. Review the current owner before trying again.";
  }
  return typedErrorGuidance[error.code];
}

export function candidateIdentityLabel(
  candidate: ReplacementCandidate
): string {
  const remoteLabel = candidate.isRemote ? "Remote" : undefined;
  return [
    candidate.fullName,
    candidate.credentials,
    candidate.facilityName,
    candidate.email,
    remoteLabel,
  ]
    .filter(Boolean)
    .join(" — ");
}

export function candidateNameLabel(candidate: ReplacementCandidate): string {
  return [candidate.fullName, candidate.credentials].filter(Boolean).join(", ");
}

export function replacementCandidateOptions(
  candidates: ReplacementCandidate[],
  currentPathologistId: string | undefined,
  selectedCandidateId: string
): ReplacementCandidateOption[] {
  const duplicateNames = duplicateCandidateNames(candidates);
  return candidates
    .filter(
      ({ id }) => id !== currentPathologistId || id === selectedCandidateId
    )
    .map((candidate) => ({
      value: candidate.id,
      label: duplicateNames.has(candidate.fullName)
        ? candidateIdentityLabel(candidate)
        : candidateNameLabel(candidate),
    }));
}

function duplicateCandidateNames(
  candidates: ReplacementCandidate[]
): Set<string> {
  const counts = new Map<string, number>();
  candidates.forEach(({ fullName }) => {
    counts.set(fullName, (counts.get(fullName) ?? 0) + 1);
  });
  return new Set(
    Array.from(counts.entries())
      .filter(([, count]) => count > 1)
      .map(([fullName]) => fullName)
  );
}

export function replacementCompletionMessage(
  response: ReplacePathologistResponse
): string {
  const priorPathologist = response.previousAssignment.pathologist.fullName;
  const replacementPathologist =
    response.currentAssignment.pathologist.fullName;
  const summary =
    `Case ${response.caseNumber}: ${priorPathologist} was replaced by ` +
    `${replacementPathologist}.`;
  const followUps = response.warnings
    .map(({ followUp }) => followUp?.trim())
    .filter((followUp): followUp is string => Boolean(followUp));
  return [summary, ...followUps].join(" ");
}

export function hasReplacementFollowUp(
  response: ReplacePathologistResponse
): boolean {
  return response.warnings.some(({ followUp }) => Boolean(followUp?.trim()));
}
